import { useEffect, useMemo, useState } from "react";
import {
  Brand,
  Button,
  Card,
  CardContent,
  Flex,
  Icon,
  Icons,
  Menu,
  Paragraph,
  Select,
  Spin,
  Switch,
  useToast,
} from "@ir/ui";
import type { OrganizationsBrandsLookupItem } from "@src/domain/dto/organizationsBrands";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { buildOrgBrandMenuItems } from "./buildOrgBrandMenuItems";
import type { RoleOption } from "./UserDetailsBrandPermissionsContent";

interface AddBrandPermissionRowProps {
  assignedBrandIds: ReadonlySet<number>;
  roleOptions: RoleOption[];
  isSaving: boolean;
  onCancel: () => void;
  onSave: (brandId: number, roleId: number, isActive: boolean) => void;
}

function filterAssignableOrgBrands(
  organizations: OrganizationsBrandsLookupItem[],
  assignedBrandIds: ReadonlySet<number>,
): OrganizationsBrandsLookupItem[] {
  return organizations
    .filter((organization) => organization.organizationIsActive)
    .map((organization) => ({
      ...organization,
      brands: (organization.brands ?? []).filter(
        (brand) => brand.brandIsActive && !assignedBrandIds.has(brand.brandId),
      ),
    }))
    .filter((organization) => (organization.brands?.length ?? 0) > 0)
    .sort((a, b) =>
      (a.organizationDisplayName ?? a.organizationName).localeCompare(
        b.organizationDisplayName ?? b.organizationName,
      ),
    );
}

function findBrandByKey(
  organizations: OrganizationsBrandsLookupItem[],
  brandKey: string,
) {
  for (const organization of organizations) {
    const brand = (organization.brands ?? []).find(
      (item) => item.brandKey === brandKey,
    );
    if (brand) {
      return {
        brand,
        organizationName:
          organization.organizationDisplayName ?? organization.organizationName,
      };
    }
  }
  return null;
}

function parseRoleId(value: unknown): number | null {
  if (typeof value === "string" || typeof value === "number") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (
    value &&
    typeof value === "object" &&
    "value" in value &&
    (typeof value.value === "string" || typeof value.value === "number")
  ) {
    const parsed = Number(value.value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

export const AddBrandPermissionRow = ({
  assignedBrandIds,
  roleOptions,
  isSaving,
  onCancel,
  onSave,
}: AddBrandPermissionRowProps) => {
  const { error: errorToast } = useToast();
  const {
    organizationsBrandsLookup,
    organizationsBrandsLookupLoading,
    organizationsBrandsLookupError,
  } = useGlobalDataLookup();

  const [selectedBrandKey, setSelectedBrandKey] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isAccessActive, setIsAccessActive] = useState(true);

  const assignableOrgs = useMemo(
    () =>
      filterAssignableOrgBrands(
        organizationsBrandsLookup ?? [],
        assignedBrandIds,
      ),
    [assignedBrandIds, organizationsBrandsLookup],
  );

  const menuItems = useMemo(
    () => buildOrgBrandMenuItems(assignableOrgs),
    [assignableOrgs],
  );

  const selectedBrand = useMemo(
    () =>
      selectedBrandKey
        ? findBrandByKey(assignableOrgs, selectedBrandKey)
        : null,
    [assignableOrgs, selectedBrandKey],
  );

  const canSave = selectedBrand != null && selectedRoleId != null && !isSaving;

  useEffect(() => {
    if (organizationsBrandsLookupError) {
      errorToast({ content: organizationsBrandsLookupError });
    }
  }, [errorToast, organizationsBrandsLookupError]);

  const handleMenuSelect = (key: string) => {
    if (findBrandByKey(assignableOrgs, key)) {
      setSelectedBrandKey(key);
    }
  };

  const handleSave = () => {
    if (selectedBrand == null || selectedRoleId == null) return;
    onSave(selectedBrand.brand.brandId, selectedRoleId, isAccessActive);
  };

  const selectedBrandLabel =
    selectedBrand?.brand.brandDisplayName ?? selectedBrand?.brand.brandName;

  let brandMenuContent = (
    <Flex align="center" gap={1} style={{ minWidth: 200 }}>
      <Icon name={Icons.Organization} />
      <Paragraph className="m-0 text-grey-500 font-bold">
        Select Brand
      </Paragraph>
    </Flex>
  );

  if (organizationsBrandsLookupLoading) {
    brandMenuContent = <Spin size="small" />;
  } else if (selectedBrand) {
    brandMenuContent = (
      <>
        <Brand
          size="small"
          variant="compact"
          brandName={selectedBrandLabel ?? ""}
          logoSrcCompact={selectedBrand.brand.brandLogoUrl?.trim() || undefined}
        />
        <Flex direction="column" style={{ minWidth: 200, textAlign: "left" }}>
          <Paragraph className="m-0 text-grey-700 font-bold text-truncate">
            {selectedBrandLabel}
          </Paragraph>
        </Flex>
      </>
    );
  }

  return (
    <Card className="mb-2">
      <CardContent className="bg-white shadow-sm border-1 border-color-grey-25 rounded-medium">
        <Flex align="center" justifyContent="space-between">
          <Flex direction="column" className="me-2" style={{ minWidth: 0 }}>
            <Menu
              items={menuItems}
              onSelect={handleMenuSelect}
              aria-label="Select organization and brand"
              placement="top start"
              style={{ maxHeight: "200px !important", overflowY: "auto" }}
            >
              <Button
                variant="clear"
                size="medium"
                color="grey"
                className="w-100 border-1 border-color-grey-25"
                isDisabled={isSaving || organizationsBrandsLookupLoading}
                style={{ justifyContent: "space-between" }}
              >
                <Flex align="center" gap={0.5} style={{ minWidth: 0 }}>
                  {brandMenuContent}
                </Flex>
                <Icon name={Icons.ChevronDown} />
              </Button>
            </Menu>
          </Flex>
          <Flex align="center" gap={0.5}>
            <Paragraph className="m-0 text-grey-500">Role:</Paragraph>
            <Select
              size="medium"
              isSearchable={false}
              isClearable={false}
              className="user-brand-role-select"
              placeholder="Select Role"
              options={roleOptions}
              isDisabled={isSaving}
              onChange={(value) => setSelectedRoleId(parseRoleId(value))}
            />
            <Switch
              size="small"
              isSelected={isAccessActive}
              onChange={setIsAccessActive}
              isDisabled={isSaving}
              aria-label="Brand access active"
              role="switch"
              htmlTitle="Brand access active"
              className="ms-1"
            />
            <Flex
              direction="column"
              align="center"
              style={{ paddingLeft: "0.25rem" }}
            >
              <Button
                size="small"
                color="primary"
                variant="clear"
                onPress={handleSave}
                isDisabled={!canSave}
                isLoading={isSaving}
                aria-label="Save brand assignment"
              >
                <Icon name={Icons.Check} />
              </Button>
              <Button
                size="small"
                color="grey"
                variant="clear"
                onPress={onCancel}
                isDisabled={isSaving}
                aria-label="Cancel add brand"
                style={{ marginLeft: "0" }}
              >
                <Icon name={Icons.Close} />
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
};
